import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { getPupils } from "redux/statisticReducer";
import { useCreateReport } from "hooks/useCreateReport";
import { declinations } from "helpers/declinations";
import { getChosenFilters, getChosenConditions } from "helpers/statistic";
import { BackButton } from "components/BackButton";
import { Range } from "components/Range";
import { Layout } from "components/Layout";
import { Loader } from "components/Loader";
import { FiltersPanel } from "components/FiltersPanel";
import search from "static/img/ic-search.svg";

/**
 * @api {get} /statistic/classes/:id статистика класса
 * @apiGroup statistic
 * @apiName статистика класса
 *
 * @apiParam {String} id ID класса
 *
 * @apiDescription <p>Страница статистики выбранного класса</p>
 * <p>Компонент <code>StatisticPupils.js</code> <a href="../client/src/pages/statistic/StatisticPupils.js">ссылка на файл</a></p>
 */

export const StatisticPupils = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation("statistic");
    const classId = useParams().id;
    const { pupilsData, filters } = useSelector((state) => state.statistic);
    const { loading } = useSelector((state) => state.app);
    const [pupils, setPupils] = useState([]);
    const [filteredPupils, setFilteredPupils] = useState([]);
    const [searchField, setSearchField] = useState("");
    const [afterSearch, setAfterSearch] = useState([]);
    const [chosenFiltersCount, setChosenFiltersCount] = useState(0);
    const { createReport, isReportLoading } = useCreateReport();
    const [showFilters, setShowFilters] = useState(false);
    const [localFilters, setLocalFilters] = useState(filters);
    const [selectedTargetGroups, setSelectedTargetGroups] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [dates, setDates] = useState([]);

    function searchHandler(e) {
        setSearchField(e.target.value);
    }

    useEffect(() => {
        dispatch(getPupils(classId));
    }, [classId, dispatch]);

    useEffect(() => {
        if (pupilsData.length) {
            setPupils(pupilsData);
        }
    }, [pupilsData]);

    // Подгрузка методик и дат при открытии фильтра
    useEffect(() => {
        if (showFilters) {
            (async () => {
                // Загружаем методики
                const arr = [];
                if (classId) {
                    arr.push(`classId=${classId}`);
                }
                if (filters.date) {
                    arr.push(`date=${filters.date}`);
                }
                const { data: conditionsData } = await axios.get(`tests/conditions-filter?${arr.join("&")}`);
                setConditions(conditionsData);

                // Загружаем даты
                const dateArr = [];
                if (classId) {
                    dateArr.push(`classId=${classId}`);
                }
                const { data: datesData } = await axios.get(`tests/dates-filter?${dateArr.join("&")}`);
                setDates(datesData);
            })();
            // Инициализируем локальные фильтры текущими значениями из стора
            setLocalFilters(filters);
            // Инициализируем выбранные целевые группы из filters.targetGroups
            if (filters.targetGroups) {
                const targetGroupIds = filters.targetGroups.split(',').filter(Boolean);
                setSelectedTargetGroups(targetGroupIds);
            } else {
                setSelectedTargetGroups([]);
            }
        }
    }, [showFilters, filters, classId]);

    useEffect(() => {
        if (classId && pupils.length) {
            let intermediateFilter = pupils.filter(
                ({ tests, isArchive }) => tests?.length && !isArchive
            );

            if (filters.sex) {
                let arr = [];

                for (let i = 0; i < intermediateFilter.length; i++) {
                    if (intermediateFilter[i].sex === filters.sex) {
                        arr.push(intermediateFilter[i]);
                    }
                }

                intermediateFilter = arr;
            }

            if (filters.years) {
                let targetAges = filters.years.replace(/\s/g, "").split(",");
                let arr = [];

                for (let i = 0; i < intermediateFilter.length; i++) {
                    let currentYears = intermediateFilter[i].birthday;
                    let date = new Date(currentYears);
                    let ageDateNumber = Date.now() - date;
                    let ageDate = new Date(ageDateNumber);
                    let year = ageDate.getUTCFullYear();
                    let age = Math.abs(1970 - year);

                    for (let j = 0; j < targetAges.length; j++) {
                        if (targetAges[j].toString() === age.toString()) {
                            arr.push(intermediateFilter[i]);
                        }
                    }
                }

                intermediateFilter = arr;
            }

            if (filters.date) {
                let arr = [];

                for (let i = 0; i < intermediateFilter.length; i++) {
                    if (intermediateFilter[i].dates.includes(filters.date)) {
                        arr.push(intermediateFilter[i]);
                    }
                }

                intermediateFilter = arr;
            }

            if (filters.targetGroups) {
                const selectedTargetGroupIds = filters.targetGroups.split(',').filter(Boolean);
                let arr = [];

                for (let i = 0; i < intermediateFilter.length; i++) {
                    // Проверяем, есть ли у ученика хотя бы одна из выбранных целевых групп (уровень 2)
                    const hasMatchingTargetGroup = intermediateFilter[i].targetGroupsLvl2?.some(pupilTargetGroup => 
                        selectedTargetGroupIds.includes(pupilTargetGroup)
                    );
                    if (hasMatchingTargetGroup) {
                        arr.push(intermediateFilter[i]);
                    }
                }

                intermediateFilter = arr;
            }

            if (filters.condition) {
                const arr = [];

                for (const pupil of intermediateFilter) {
                    if (pupil.conditions?.includes(filters.condition)) {
                        arr.push(pupil);
                    }
                }

                intermediateFilter = arr;
            }

            // Подсчет активных фильтров
            let count = 0;
            if (filters.sex) count++;
            if (filters.years) count++;
            if (filters.targetGroups) {
                count += filters.targetGroups.split(',').filter(Boolean).length;
            }
            if (filters.date) count++;
            if (filters.condition) count++;

            setChosenFiltersCount(count);
            setFilteredPupils(intermediateFilter);
        }
    }, [classId, filters, pupils]);

    useEffect(() => {
        if (searchField.length > 0) {
            let newPupils = [];
            for (let pupil of filteredPupils) {
                let name = pupil.name;
                let slicedName = name.slice(0, searchField.length);
                let lastName = pupil.surname;
                let slicedLast = lastName.slice(0, searchField.length);
                if (
                    slicedName.toLowerCase() === searchField.toLowerCase() ||
                    slicedLast.toLowerCase() === searchField.toLowerCase()
                ) {
                    newPupils.push(pupil);
                }
            }
            setAfterSearch(newPupils);
        } else {
            setAfterSearch(filteredPupils);
        }
    }, [searchField, filteredPupils]);

    const filterChangeHandler = (e) => {
        if (e.target.name === "years") {
            const pattern = /^[,0-9 ]*$/i;
            const inputValid = pattern.test(e.target.value.replace(/ /g, ""));
            if (!inputValid) return;
        }
        setLocalFilters({ ...localFilters, [e.target.name]: e.target.value });
    };

    const filterTargetGroupHandler = (evt) => {
        const targetGroupId = evt.target.id;
        let newSelectedTargetGroups;

        if (selectedTargetGroups.includes(targetGroupId)) {
            // Убираем целевую группу из выбранных
            newSelectedTargetGroups = selectedTargetGroups.filter(id => id !== targetGroupId);
        } else {
            // Добавляем целевую группу к выбранным
            newSelectedTargetGroups = [...selectedTargetGroups, targetGroupId];
        }

        setSelectedTargetGroups(newSelectedTargetGroups);
        // Сохраняем массив целевых групп как строку через запятую
        setLocalFilters({ ...localFilters, targetGroups: newSelectedTargetGroups.join(',') });
    };

    const filterTargetGroupSelectAllHandler = (groupIds, shouldSelect) => {
        setSelectedTargetGroups(prev => {
            let newSelectedTargetGroups;
            
            if (shouldSelect) {
                // Добавляем все группы, которых еще нет в выбранных
                newSelectedTargetGroups = [...new Set([...prev, ...groupIds])];
            } else {
                // Убираем все указанные группы
                newSelectedTargetGroups = prev.filter(id => !groupIds.includes(id));
            }
            
            setLocalFilters(prevFilters => ({ 
                ...prevFilters, 
                targetGroups: newSelectedTargetGroups.join(',') 
            }));
            
            return newSelectedTargetGroups;
        });
    };

    const conditionChangeHandler = (conditionId) => {
        if (conditionId === localFilters.condition) {
            // Если кликнули на уже выбранную методику, сбрасываем
            setLocalFilters({ ...localFilters, condition: "", scale: "", level: 0 });
        } else {
            // Выбираем новую методику
            setLocalFilters({ ...localFilters, condition: conditionId, scale: "", level: 0 });
        }
    };

    const applyFilters = () => {
        dispatch({
            type: "STATISTIC/SET_FILTERS",
            payload: { ...localFilters },
        });
        setShowFilters(false);
    };

    const resetLocalFilters = () => {
        const resetFilters = { sex: "", years: "", targetGroups: "", condition: "", date: "" };
        setLocalFilters(resetFilters);
        setSelectedTargetGroups([]);
        // Сразу применяем сброшенные фильтры
        dispatch({
            type: "STATISTIC/SET_FILTERS",
            payload: resetFilters,
        });
        setShowFilters(false);
    };

    const closeFilters = () => setShowFilters(false);

    const createReportHandler = () => {
        const classInfo = pupilsData[0]?.class;
        const className = classInfo ? `${classInfo.number}${classInfo.letter}` : "";
        const pupils = afterSearch.map(({ _id }) => _id).join(",");
        const date = filters.date || "";
        const chosenFilters = JSON.stringify(filters);
        const chosenConditions = getChosenConditions(filters.condition, afterSearch);

        // Используем POST запрос с данными в body
        // pupils и conditions уже строки с запятыми
        const reportData = {
            pupils, // уже строка вида "id1,id2,id3"
            conditions: chosenConditions, // уже строка вида "id1,id2,id3"
            date,
            filters: chosenFilters
        };

        createReport(
            `statistic/psych-report`,
            `${t("class_report")}_${className}.xlsx`,
            reportData,
            "post"
        );
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton />
                    <p className="page__title">{t("statistic")}</p>

                    <button
                        onClick={() => setShowFilters((v) => !v)}
                        className={
                            !!chosenFiltersCount
                                ? `icon-btn page__icon-btn page__icon-btn_right icon-btn_filter icon-btn_filter_active`
                                : `icon-btn page__icon-btn page__icon-btn_right icon-btn_filter`
                        }
                    >
                        {!!chosenFiltersCount && (
                            <span>
                                {chosenFiltersCount}
                                <div className="dot"></div>
                            </span>
                        )}
                    </button>
                </header>

                {!showFilters && (
                    <div>
                        {!!afterSearch.length && (
                            <div
                                className="statistic-reports__btn statistic-reports__btn-class"
                                onClick={createReportHandler}
                            >
                                {isReportLoading ? (
                                    <p>{t("report_loading", { ns: "translation" })}</p>
                                ) : (
                                    <p>{t("create_report", { ns: "translation" })}</p>
                                )}
                            </div>
                        )}

                        <div className="school-page__search">
                            <img src={search} alt="" />
                            <input
                                onChange={searchHandler}
                                value={searchField}
                                placeholder={t("search_placeholder")}
                            />
                        </div>

                        {afterSearch.length ? (
                            afterSearch.map((pupil) => (
                                <Link
                                    key={pupil._id}
                                    to={`/statistic/pupils/${pupil._id}`}
                                    className="statistic-condition"
                                >
                                    <p className="statistic-condition__title">
                                        {pupil.surname} {pupil.name}
                                    </p>

                                    <Range
                                        total={pupil.tests.length}
                                        norm={pupil.doneTestQuantity}
                                        // notNorm={pupil.negatively}
                                        notNorm={pupil.tests.length - pupil.doneTestQuantity}
                                    />

                                    <div className="statistic-condition__footer">
                                        <span className="statistic-condition__indicator green">
                                            {pupil.doneTestQuantity}
                                        </span>
                                        <span className="statistic-condition__indicator blue">
                                            {pupil.tests.length - pupil.doneTestQuantity}
                                        </span>

                                        <p className="statistic-condition__members">
                                            {pupil.tests.length}{" "}
                                            {declinations(pupil.tests.length, "test", i18n.language)}
                                        </p>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <p className="statistic-condition-text">{t("not_found")}</p>
                        )}
                    </div>
                )}

                {showFilters && (
                    <FiltersPanel
                        value={localFilters}
                        onChange={filterChangeHandler}
                        onTargetGroupToggle={filterTargetGroupHandler}
                        onTargetGroupSelectAll={filterTargetGroupSelectAllHandler}
                        onApply={applyFilters}
                        onReset={resetLocalFilters}
                        onClose={closeFilters}
                        selectedTargetGroups={selectedTargetGroups}
                        conditions={conditions}
                        dates={dates}
                        onConditionChange={conditionChangeHandler}
                    />
                )}
            </div>
        </Layout>
    );
};
